
import { Component, Vue, Prop } from 'vue-property-decorator';

interface LifecycleStatus {
  key: string;
  label: string;
  color: string;
  icon: string;
  description: string;
  incomingInventory: boolean;
  actions: string[];
  warning?: string;
  transitions: string[];
}

@Component({
  name: 'POLifecycleGuide'
})
export default class POLifecycleGuide extends Vue {
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: String, default: 'draft' }) readonly currentStatus!: string;

  private readonly statusOrder = ['draft', 'confirmed', 'partial_received', 'received', 'completed'];

  private readonly lifecycleStatuses: LifecycleStatus[] = [
    {
      key: 'draft',
      label: 'Draft',
      color: 'grey',
      icon: 'mdi-file-document-edit-outline',
      description: 'Initial state when PO is created.',
      incomingInventory: false,
      actions: [
        'Edit all details (supplier, items, quantities, prices)',
        'Add or remove items',
        'Send email to supplier',
        'Confirm to start ordering process',
        'Duplicate to create a new draft PO',
        'Cancel if no longer needed'
      ],
      transitions: ['confirmed', 'canceled']
    },
    {
      key: 'confirmed',
      label: 'Confirmed',
      color: 'purple',
      icon: 'mdi-check-circle-outline',
      description: 'PO is confirmed and ready for receiving.',
      incomingInventory: true,
      actions: [
        'Receive items as they arrive',
        'Send/resend email to supplier',
        'Revert to Draft for modifications',
        'Duplicate to create a new draft PO',
        'Cancel if supplier cannot fulfill'
      ],
      transitions: ['draft', 'partial_received', 'received', 'canceled']
    },
    {
      key: 'partial_received',
      label: 'Partial Received',
      color: 'orange',
      icon: 'mdi-package-variant',
      description: 'Some items have been received.',
      incomingInventory: true,
      actions: [
        'Continue receiving more items',
        'Return items to supplier',
        'Duplicate to create a new draft PO',
        'Mark as Complete if done'
      ],
      warning: 'Cannot cancel once receiving starts',
      transitions: ['received', 'completed']
    },
    {
      key: 'received',
      label: 'Received',
      color: 'green',
      icon: 'mdi-package-check',
      description: 'All ordered quantities received.',
      incomingInventory: false,
      actions: [
        'Return items to supplier if needed',
        'Duplicate to create a new draft PO',
        'Mark as Complete to close PO'
      ],
      transitions: ['completed', 'partial_received']
    },
    {
      key: 'completed',
      label: 'Completed',
      color: 'teal darken-2',
      icon: 'mdi-check-circle',
      description: 'PO is finalized. Terminal state.',
      incomingInventory: false,
      actions: [
        'Return items to supplier if needed',
        'Duplicate to create a new draft PO',
        'Can still send emails for records',
        'Can export/print for records'
      ],
      transitions: []
    },
    {
      key: 'canceled',
      label: 'Canceled',
      color: 'red',
      icon: 'mdi-cancel',
      description: 'PO has been canceled. Terminal state.',
      incomingInventory: false,
      actions: [
        'Duplicate to create a new draft PO',
        'View-only: no other changes allowed'
      ],
      transitions: []
    }
  ];

  get mainFlowStatuses(): LifecycleStatus[] {
    return this.lifecycleStatuses.filter(s => s.key !== 'canceled');
  }

  isStatusCompleted(statusKey: string): boolean {
    const currentIndex = this.statusOrder.indexOf(this.currentStatus);
    const targetIndex = this.statusOrder.indexOf(statusKey);
    return currentIndex > targetIndex;
  }

  getStatusHexColor(vuetifyColor: string): string {
    const colorMap: Record<string, string> = {
      'grey': '#9e9e9e',
      'purple': '#9c27b0',
      'orange': '#ff9800',
      'green': '#4caf50',
      'teal': '#009688',
      'teal darken-2': '#00796b',
      'red': '#f44336'
    };
    return colorMap[vuetifyColor] || '#9e9e9e';
  }

  getStatusCircleStyle(status: LifecycleStatus): Record<string, string> {
    const color = this.getStatusHexColor(status.color);
    if (this.currentStatus === status.key) {
      return {
        borderColor: color,
        backgroundColor: color,
        boxShadow: `0 4px 16px ${color}40`
      };
    }
    return {
      borderColor: color,
      backgroundColor: 'white'
    };
  }
}
